<template>
  <dialog ref="dialog" @click="onBackdropClick">
    <div class="contents">
      <h3>Upload run from GPX</h3>
      <p class="hint">
        Upload a GPX track from a watch or phone export to log it as a completed
        run on this course.
      </p>

      <FormKit
        id="import-run-track"
        type="form"
        :submit-label="submitting ? 'Uploading…' : 'Upload run'"
        :disabled="submitting"
        @submit="onSubmit"
      >
        <FormKit
          type="file"
          name="file"
          accept=".gpx,application/gpx+xml"
          label="GPX file"
          validation="required"
        />
      </FormKit>
    </div>
  </dialog>
</template>
<script lang="ts" setup>
import { setErrors } from "@formkit/vue";

const props = defineProps<{ courseId: string }>();
const emit = defineEmits<{ close: []; uploaded: [runId: string] }>();

const dialog = ref<HTMLDialogElement>();
const submitting = ref(false);
const supabase = useClient();
const router = useRouter();

onMounted(() => dialog.value?.showModal());

// Native <dialog> sees clicks landing on the dialog's padding strip as
// targeting the dialog itself. Only treat clicks where the actual target is
// the dialog element (i.e. true backdrop clicks) as a dismiss.
function onBackdropClick(e: MouseEvent) {
  if (e.target === dialog.value) emit("close");
}

async function onSubmit(values: { file: { file: File }[] }) {
  setErrors("import-run-track", []);
  submitting.value = true;
  try {
    const file = values.file[0]?.file;
    if (!file) throw new Error("No file selected");

    // Force the user JWT onto the request — supabase-js's functions client
    // sometimes lags behind auth state and falls back to the anon key, which
    // makes the function see an unauthenticated request.
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error("You must be signed in to upload a track");

    const form = new FormData();
    form.append("course_id", props.courseId);
    form.append("file", file);

    const { data, error } = await supabase.functions.invoke<{
      run_id: string;
      checkin_count: number;
      point_count: number;
    }>("import-track", {
      body: form,
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    if (error) throw error;
    const runId = data?.run_id;
    if (!runId) throw new Error("Server did not return a run_id");

    emit("uploaded", runId);
    router.push(`/runs/${runId}`);
  } catch (err: unknown) {
    setErrors("import-run-track", [
      err instanceof Error ? err.message : String(err),
    ]);
  } finally {
    submitting.value = false;
  }
}
</script>
<style scoped>
dialog {
  border-radius: var(--border-radius);
  border: var(--border);
  box-shadow: var(--prominent-shadow);
  padding: var(--grid);
  min-width: min(420px, 90vw);
}

dialog::backdrop {
  background: rgba(231, 230, 226, 0.4);
}

.contents {
  display: flex;
  flex-direction: column;
  gap: var(--half-grid);
}

.hint {
  color: var(--medium-gray);
  margin: 0;
}
</style>
